import { SwapValidationErrorDto } from "../../../../api-client";

/**
 * Stable key describing which prescription (Rx) a timeslot bills to.
 * The swap only changes the Rx link, never the calendar date, so comparing
 * the set of rx ids tells us whether an appointment's billing actually changed.
 */
export function rxOfTs(ts: any): string {
  return String(ts?.therapyRx?.rx?.id ?? ts?.therapyRx?.rx?.rxNumber ?? "?");
}

export interface FilterChangedResult {
  changedBefore: any[];
  changedAfter: any[];
  unchangedCount: number;
}

/**
 * Compares before/after at the timeSlot level, matched by timeSlot.id.
 *
 * The swap only re-links Treatment.therapyRx; the Treatment (timeSlot) row keeps
 * its id, so timeSlot.id is the only stable key across both payloads. Appointment
 * ids are unreliable here: the backend's after-projection groups by appointment
 * and DROPS unscheduled/cancelled timeslots, so those appear only in "before".
 *
 * A timeSlot counts as changed only when it exists in BOTH lists with a different
 * Rx. Timeslots missing from "after" cannot be judged (no data) and are treated
 * as unchanged to avoid false positives. An appointment is shown as changed if it
 * contains at least one changed timeSlot.
 */
export function filterChanged(beforeAppts: any[], afterAppts: any[]): FilterChangedResult {
  const afterRxByTsId = new Map<any, string>();
  for (const a of afterAppts || []) {
    for (const ts of a?.timeSlots || []) {
      if (ts?.id != null) afterRxByTsId.set(ts.id, rxOfTs(ts));
    }
  }

  const changedTsIds = new Set<any>();
  for (const b of beforeAppts || []) {
    for (const ts of b?.timeSlots || []) {
      if (ts?.id == null) continue;
      const afterRx = afterRxByTsId.get(ts.id);
      if (afterRx === undefined) continue; // not in "after" -> cannot judge -> unchanged
      if (afterRx !== rxOfTs(ts)) changedTsIds.add(ts.id);
    }
  }

  const apptHasChange = (a: any) =>
    (a?.timeSlots || []).some((ts: any) => ts?.id != null && changedTsIds.has(ts.id));

  const changedBefore = (beforeAppts || []).filter(apptHasChange);
  const changedAfter = (afterAppts || []).filter(apptHasChange);
  const unchangedCount = (beforeAppts || []).length - changedBefore.length;

  return { changedBefore, changedAfter, unchangedCount };
}

export interface GroupedSwapErrors {
  rxNumber: string;
  items: SwapValidationErrorDto[];
}

/**
 * Group swap-introduced validation errors by Rx so each prescription is shown
 * once, sorted by rxNumber (numeric-aware).
 */
export function groupSwapValidationErrors(
  errors: SwapValidationErrorDto[],
): GroupedSwapErrors[] {
  const byRx = new Map<string, GroupedSwapErrors>();
  for (const err of errors) {
    const entry = byRx.get(err.rxId) ?? { rxNumber: err.rxNumber, items: [] };
    entry.items.push(err);
    byRx.set(err.rxId, entry);
  }
  return Array.from(byRx.values()).sort((a, b) =>
    a.rxNumber.localeCompare(b.rxNumber, undefined, { numeric: true }),
  );
}

export type SwapStep = "completion" | "result" | "close";

/**
 * Which post-swap step to surface. Only one shows at a time, in precedence order:
 * actionable completion dialog → validation result modal → close (success toast).
 */
export function nextSwapStep(
  completableCount: number,
  validationErrorCount: number,
): SwapStep {
  if (completableCount > 0) return "completion";
  if (validationErrorCount > 0) return "result";
  return "close";
}
